// src/services/image.service.js
import { storage } from "../firebase/config";
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import apiService from './api.service';

class ImageService {
  async uploadImages(files) {
    try {
      console.log('INICIANDO SUBIDA DE IMÁGENES');
      console.log('Archivos recibidos:', files);
      
      // Verificaciones iniciales
      if (!storage) {
        throw new Error('Firebase Storage no está inicializado');
      }
      
      if (!Array.isArray(files)) {
        throw new Error('El parámetro files debe ser un array');
      }
      
      if (files.length === 0) {
        console.log('No hay archivos para subir');
        return [];
      }

      // Filtrar archivos válidos
      const validFiles = files.filter(file => {
        if (!file) {
          console.warn('Archivo undefined encontrado, filtrando...');
          return false;
        }
        if (!(file instanceof File)) {
          console.warn('Archivo no es instancia de File:', file);
          return false;
        }
        if (!file.type.startsWith('image/')) {
          console.warn('Archivo no es una imagen:', file.name, file.type);
          return false;
        }
        return true;
      });

      console.log('Archivos válidos después de filtrar:', validFiles.length);

      if (validFiles.length === 0) {
        throw new Error('No hay archivos de imagen válidos para subir');
      }

      const uploadPromises = validFiles.map(async (file, index) => {
        try {
          console.log(`📤 Procesando archivo ${index + 1}/${validFiles.length}:`, {
            nombre: file.name,
            tamaño: file.size,
            tipo: file.type
          });
          
          // Validar tamaño (máximo 5MB)
          const maxSize = 5 * 1024 * 1024;
          if (file.size > maxSize) {
            throw new Error(`La imagen ${file.name} es demasiado grande (${(file.size / 1024 / 1024).toFixed(2)}MB, máximo 5MB)`);
          }

          // Crear nombre único para el archivo
          const timestamp = Date.now();
          const randomString = Math.random().toString(36).substring(2, 15);
          const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
          const fileName = `denuncias/${timestamp}_${randomString}_${safeFileName}`;
          
          console.log('Subiendo a Firebase Storage:', fileName);
          
          // Crear referencia en Storage
          console.log('Creando referencia de storage...');
          const storageRef = ref(storage, fileName);
          console.log('Referencia creada:', storageRef);
          
          // Subir archivo
          console.log('Subiendo bytes...');
          const snapshot = await uploadBytes(storageRef, file);
          console.log('Archivo subido exitosamente:', fileName);
          
          // Obtener URL de descarga
          console.log('Obteniendo URL de descarga...');
          const downloadURL = await getDownloadURL(snapshot.ref);
          console.log('URL obtenida:', downloadURL);
          
          return {
            id: `temp_${timestamp}_${index}`,
            name: file.name,
            url: downloadURL,
            path: fileName // Guardamos el path que usamos
          };
        } catch (fileError) {
          console.error(`Error subiendo archivo ${file?.name || 'desconocido'}:`, fileError);
          throw new Error(`Error con ${file?.name || 'archivo'}: ${fileError.message}`);
        }
      });

      const results = await Promise.all(uploadPromises);
      console.log('Todas las imágenes subidas exitosamente:', results.length, 'imágenes');
      return results;
    } catch (error) {
      console.error('ERROR CRÍTICO en uploadImages:', error);
      throw new Error(`No se pudieron subir las imágenes: ${error.message}`);
    }
  }

  async createImageRecords(imagenes, reclamoId) {
    try {
      console.log('Creando registros de imágenes para reclamo:', reclamoId);
      
      if (!imagenes || imagenes.length === 0) {
        console.log('No hay imágenes para registrar');
        return [];
      }

      const createPromises = imagenes.map(imagen =>
        apiService.request('/imagenes', {
          method: 'POST',
          body: JSON.stringify({
            url: imagen.url,
            reclamo_id: reclamoId,
            nombre: imagen.name,
            path: imagen.path
          })
        })
      );

      const results = await Promise.all(createPromises);
      console.log('Registros de imágenes creados en la base de datos:', results.length);
      return results;
    } catch (error) {
      console.error('Error creando registros de imágenes:', error);
      
      // Limpiar imágenes subidas si falla
      if (imagenes && imagenes.length > 0) {
        console.log('Limpiando imágenes subidas después del error...');
        try {
          await this.deleteImages(imagenes);
          console.log('Imágenes limpiadas exitosamente');
        } catch (deleteError) {
          console.error('Error limpiando imágenes:', deleteError);
        }
      }
      
      throw new Error('No se pudieron guardar las imágenes en el sistema: ' + error.message);
    }
  }

  async deleteImages(imagenes) {
    try {
      if (!imagenes || imagenes.length === 0) {
        console.log('No hay imágenes para eliminar');
        return;
      }

      console.log('Eliminando imágenes de storage:', imagenes.length);
      
      const deletePromises = imagenes.map(async (imagen) => {
        if (!imagen) {
          console.warn('Imagen inválida para eliminar');
          return;
        }

        try {
          let pathToDelete = imagen.path;
          
          // Si no tenemos path, intentar extraer de la URL
          if (!pathToDelete && imagen.url) {
            pathToDelete = this.extractPathFromFirebaseURL(imagen.url);
          }
          
          if (!pathToDelete) {
            console.warn('No se puede determinar el path para eliminar:', imagen);
            return;
          }

          console.log('Eliminando imagen con path:', pathToDelete);
          const imageRef = ref(storage, pathToDelete);
          await deleteObject(imageRef);
          console.log('Imagen eliminada exitosamente');
        } catch (storageError) {
          console.warn('Error eliminando imagen de storage:', storageError.message);
        }
      });

      await Promise.allSettled(deletePromises);
      console.log('Proceso de eliminación completado');
    } catch (error) {
      console.error('Error en proceso de eliminación:', error);
    }
  }

  extractPathFromFirebaseURL(url) {
    try {
      const urlObj = new URL(url);
      const pathSegment = urlObj.pathname.split('/o/')[1];
      if (pathSegment) {
        // Decodificar el path (los %2F se convierten en /)
        return decodeURIComponent(pathSegment.split('?')[0]);
      }
      return null;
    } catch (error) {
      console.error('Error extrayendo path de URL:', error);
      return null;
    }
  }
}

const imageService = new ImageService();
export default imageService;